import AsyncStorage from '@react-native-async-storage/async-storage';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import FilledButton from '../../components/FilledButton';
import MyScrollView from '../../components/MyScrollView';
import PtfKeyboardAvoidingView from '../../components/PtfKeyboardAvoidingView';
import Svg from '../../components/Svg';
import MyFloatingLabelInput from '../../components/form/MyFloatingLabelInput';
import Header from '../../components/register/Header';
import Colors from '../../constants/Colors';
import Languages from '../../constants/Languages';
import Layout from '../../constants/Layout';
import * as Actions from '../../store/Actions';
import { formatPhoneNumber } from '../../utils/formatPhone';

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.mediumBlue,
  },
  mainContentContainer: {
    alignItems: 'center',
    backgroundColor: Colors.white,
  },
  topContainer: {
    alignItems: 'center',
  },
  phoneContainer: {
    width: 328,
    paddingTop: 40,
  },
  text: {
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 20 : 26,
    color: Colors.veryDarkGray,
    textAlign: 'center',
    marginTop: Layout.isSmall ? 80 : 50,
    width: Layout.isSmall ? 225 : 500,
  },
  image: {
    height: 101,
    width: 101,
    resizeMode: 'contain',
    marginTop: Layout.isSmall ? 33 : 79,
    marginLeft: -10,
  },
  form: {
    marginTop: Layout.isSmall ? 24 : 52,
    marginLeft: -15, // to align inputs within form
    marginBottom: 16,
    padding: 0,
    width: 345,
  },
  input: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'roboto-regular',
  },
  signupContainer: Layout.signupContainer,
  signupFooter: Layout.signupFooter,
  linkContainer: {
    paddingHorizontal: 16,
    paddingTop: Layout.isSmall ? 20 : 32,
    paddingBottom: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  doNotAskContainer: {
    flexDirection: 'row',
  },
  checkbox: {
    width: 32,
  },
  label: {
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 16 : 14,
    color: Colors.veryDarkGray,
  },
  linkTextContainer: {
    marginBottom: 24,
  },
  linkText: {
    fontSize: Layout.isSmall ? 14 : 16,
    color: Colors.darkGreen,
    textDecorationLine: 'underline',
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  errorLabel: {
    marginBottom: 8,
  },
  smsDisclaimer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    alignItems: 'center',
  },
  smsDisclaimerText: {
    fontSize: 12,
    color: Colors.veryDarkGray,
    fontFamily: 'roboto-regular',
    textAlign: 'center',
  },
});

class EnterPhoneScreen extends Component {
  static propTypes = {
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
    navigation: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  constructor(props) {
    super();
    this.state = {
      phone: '',
      isValid: false,
      error: '',
      doNotAskAgain: false,
    };
    this.scroll = null;
  }

  validate = (value) => {
    if (!value) return false;
    // phone, must be 10
    return value.length === 10;
  };

  goBack = () => {
    const { navigation } = this.props;
    navigation.navigate('Language');
  };

  goNext = () => {
    const { actions, navigation, registration } = this.props;
    const { flow, user, language } = registration;
    const translation = Languages.translations[language?.name || 'en'];
    const { phone, isValid } = this.state;
    if (isValid) {
      actions.enterPhone(phone, (response) => {
        if (response.error) {
          this.setState({
            error: translation[response.error] || response.error,
          });
        } else if (response.results.sms_enabled) {
          navigation.navigate('OtpCode');
        } else if (flow === 'new_email') {
          navigation.navigate('TermsPrivacy');
        } else if (user.docs && user.docs.length) {
          navigation.navigate('TermsPrivacy');
        } else if (!user.onboarding_complete) {
          navigation.navigate('UserDetails');
        } else {
          AsyncStorage.setItem('onboarded', '1').then(() => {
            actions.refreshRedux();
            navigation.navigate('a');
          });
        }
      });
    }
    // if existing phone and authenticated through otp, we link the accounts (move email to existing)
  };

  skip = () => {
    const { registration, actions, navigation } = this.props;
    const { doNotAskAgain } = this.state;
    const { user, flow } = registration;

    if (doNotAskAgain) {
      actions.doNotAskAgain('phone');
    }
    if (flow === 'new_email') {
      navigation.navigate('TermsPrivacy');
    } else if (user.docs && user.docs.length) {
      navigation.navigate('TermsPrivacy');
    } else if (!user.onboarding_complete) {
      navigation.navigate('UserDetails');
    } else {
      AsyncStorage.setItem('onboarded', '1').then(() => {
        actions.refreshRedux();
        navigation.navigate('a');
      });
    }
  };

  onUpdateText = (text) => {
    const { actions, registration } = this.props;
    const { enterPhone } = registration;
    if (enterPhone.error) actions.clearEnterPhoneError();
    let value = text.trim();
    if (value && value[0] === '(') {
      value = value.replace(/\D/g, '');
    }
    if (value.length > 10) value = value.substring(0, 10);
    const isValid = this.validate(value);
    value = formatPhoneNumber(value);
    this.setState({ phone: value, isValid, error: null });
  };

  onPressDoNotAsk = () => {
    const { doNotAskAgain } = this.state;
    this.setState({ doNotAskAgain: !doNotAskAgain });
  };

  render() {
    const { registration } = this.props;
    const { phone, isValid, doNotAskAgain, error } = this.state;
    const { language, enterPhone } = registration;
    const translation = Languages.translations[language?.name || 'en'];
    const phoneNumberOptional = `${translation.app_personal_edittext_phone} (${translation.optional})`;

    return (
      <SafeAreaView style={{ flex: 1 }} edges={['top', 'left', 'right', 'bottom']}>
        <PtfKeyboardAvoidingView style={{ flex: 1 }}>
          <MyScrollView
            contentContainerStyle={styles.mainContentContainer}
            containerStyle={styles.container}
            testID="enter-phone"
          >
            <Header title={translation.sign_in} goBack={this.goBack} />
            <View style={styles.signupContainer}>
              <View style={styles.topContainer}>
                <Image source={require('../../assets/images/sign_up/image_36.png')} style={styles.image} />
                <Text style={styles.text}>{translation.enter_phone}</Text>
                <View style={styles.phoneContainer}>
                  <MyFloatingLabelInput
                    label={phoneNumberOptional}
                    width={328}
                    onChangeText={this.onUpdateText}
                    onSubmitEditing={this.goNext}
                    keyboardType="phone-pad"
                    value={phone}
                    autoFocus
                    testID="phone"
                  />
                  {enterPhone.error ? (
                    <Text style={styles.errorLabel}>{translation[enterPhone.error]}</Text>
                  ) : null}
                </View>
              </View>
              <View style={styles.signupFooter}>
                <FilledButton
                  onPress={this.goNext}
                  label={translation.fragment_registration_language_button_next}
                  color={Colors.primaryColor}
                  full
                  textColor={Colors.white}
                  disabled={!isValid || error}
                />
                <View style={styles.smsDisclaimer}>
                  <Text style={styles.smsDisclaimerText}>{translation.sms_disclaimer}</Text>
                </View>
                <View style={styles.linkContainer}>
                  <TouchableOpacity onPress={this.skip} style={styles.linkTextContainer} testID="skip">
                    <Text style={styles.linkText}>{translation.skip}</Text>
                  </TouchableOpacity>
                  <View style={styles.doNotAskContainer}>
                    <TouchableOpacity
                      style={styles.checkbox}
                      onPress={this.onPressDoNotAsk}
                      testID="do-not-ask"
                    >
                      <Svg
                        icon={doNotAskAgain ? 'square_checked' : 'square_unchecked'}
                        style={styles.checkbox}
                      />
                    </TouchableOpacity>
                    <Text style={styles.label}>{translation.do_not_ask_again}</Text>
                  </View>
                </View>
              </View>
            </View>
          </MyScrollView>
        </PtfKeyboardAvoidingView>
      </SafeAreaView>
    );
  }
}

function mapStateToProps(state) {
  return {
    registration: state.registration,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EnterPhoneScreen);
